/**
 * Keeps the workspace-scoped stores — items, pools, sessions, tracks, attributes,
 * workflows, cycle templates and the rest — loaded for the current workspace, and the permission
 * scope pointed at it. Runs when a shell mounts with a workspace and again
 * whenever the workspace changes, so every store reflects the workspace the
 * person is in.
 *
 * The Priorities layout carried this as an inline effect. The Mandate shell
 * embeds the same platform pages (items, sessions, tools, lineage) but never
 * loaded their stores, so a cold load of /apps/mandate/sessions read
 * "0 sessions" over a space that has two (staging, 2026-11-02). One copy, both
 * shells.
 */

import { useEffect, useRef } from 'react';
import { getTenantContext, setTenantContext } from '../lib/tenantContext';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { useUserStore } from '../stores/prioritization/userStore';
import { usePrioritizationTaskStore } from '../stores/prioritization/prioritizationTaskStore';
import { useDegapProcessStore } from '../stores/prioritization/degapProcessStore';
import { useSavedViewsStore } from '../stores/prioritization/savedViewsStore';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { useItemCategoriesStore } from '../stores/prioritization/itemCategoriesStore';
import { useCycleConfigStore } from '../stores/prioritization/cycleConfigStore';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useEvidenceStore } from '../stores/prioritization/evidenceStore';
import { useTriageStore } from '../stores/prioritization/triageStore';
import { useSessionStore } from '../stores/prioritization/sessionStore';
import { useTrackStore } from '../stores/prioritization/trackStore';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useWorkflowStore } from '../stores/prioritization/workflowStore';
import { useCycleTemplateStore } from '../stores/prioritization/cycleTemplateStore';
import { useStrategyStore } from '../stores/prioritization/strategyStore';
import { useEnterpriseIdentityStore } from '../stores/prioritization/enterpriseIdentityStore';

/** Fetch every workspace-scoped store for `workspaceId`; errors are logged, never thrown. */
export function refetchWorkspaceScopedStores(workspaceId: string): Promise<void> {
  return Promise.all([
    useDegapProcessStore.getState().fetchFromSupabase?.(),
    useItemsStore.getState().fetchFromSupabase?.(),
    useItemPoolsStore.getState().fetchFromSupabase?.(),
    useSessionStore.getState().fetchFromSupabase?.(),
    useTrackStore.getState().fetchAllTracks?.(),
    useAttributesStore.getState().fetchFromSupabase?.(),
    // Workflows answer for one workspace (the Workflows settings page, cycle
    // creation, the sidebar). This line used to call a fetchFromSupabase the
    // workflow store never had, so a workspace change left the old workspace's.
    useWorkflowStore.getState().fetchWorkflows(),
    useTriageStore.getState().fetchFromSupabase?.(),
    useStrategyStore.getState().fetchFromSupabase?.(),
    useEvidenceStore.getState().fetchFromSupabase?.(),
    useSavedViewsStore.getState().fetchFromSupabase?.(),
    usePrioritizationTaskStore.getState().loadTasksFromSupabase?.(),
    useCycleConfigStore.getState().loadSessionsFromSupabase?.(),
    useEnterpriseIdentityStore.getState().fetchFromSupabase?.({ workspaceId }),
    // The labels this workspace gave each category's altitude levels ("Altitude 3 · Release"):
    // every screen that names an altitude reads them from here.
    useItemCategoriesStore.getState().fetchCategories?.(workspaceId),
  ].filter(Boolean))
    .then(() => undefined)
    .catch((err) => {
      console.error('[WorkspaceScopedStores] refetch failed:', err);
    });
}

export function useWorkspaceScopedStores(): void {
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);
  const prevWorkspaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    const prev = prevWorkspaceRef.current;
    if (prev !== null && prev !== currentWorkspaceId) {
      useItemsStore.setState({ itemsFetchWorkspaceId: null });
    }
    prevWorkspaceRef.current = currentWorkspaceId;
    // Platform/system admins have no workspace membership, so ensureWorkspaceContext() never sets a
    // tenant context for them. When they explicitly select a workspace via the switcher, initialize
    // the tenant context directly so getCurrentWorkspaceId() returns the chosen workspace and all
    // workspace-scoped data fetches run instead of being skipped.
    if (!getTenantContext()) {
      const { userId, userEmail, userName } = useUserStore.getState();
      const ws = useTenantStore.getState().workspaces.find((w) => w.id === currentWorkspaceId);
      if (userId && userId !== 'unknown') {
        setTenantContext({
          workspaceId: currentWorkspaceId,
          workspaceName: ws?.name,
          userId,
          userEmail: userEmail || undefined,
          userName: userName || undefined,
        });
      }
    }

    useUserStore.getState().setCurrentScope?.({
      kind: 'resource',
      id: currentWorkspaceId,
      parentScope: { kind: 'organization', id: '*' },
    });
    useUserStore.getState().loadPermissions?.();

    void refetchWorkspaceScopedStores(currentWorkspaceId);
    // Cycle templates are read only by the screens that show them; once read,
    // they belong to a workspace and are read again for the new one.
    if (prev !== null && prev !== currentWorkspaceId) {
      const templates = useCycleTemplateStore.getState();
      if (templates.loadedWorkspaceId !== null || templates.templates.length > 0 || templates.isLoading) {
        void templates.fetchTemplates();
      }
    }
  }, [currentWorkspaceId]);
}
